import axios from "axios";
import Cookies from "js-cookie";
import getAccessToken from "./accessToken";
import CryptoJS from "crypto-js";

const axiosApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// ---------- REQUEST INTERCEPTOR ----------
axiosApi.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = token;
  }

  const lang = Cookies.get("I18N_LANGUAGE");
  config.headers["Accept-Language"] = lang === "ar" ? "ar" : "en";

  // ---------------- HMAC LOGIC ----------------
  const secret = process.env.REACT_APP_HMAC_SECRET;

  const timestamp = Date.now();
  const nonce =
    timestamp.toString() + CryptoJS.lib.WordArray.random(8).toString();

  const method = (config.method || "GET").toUpperCase();

  let rawString = "";
  let bodyString = "";

  if (["POST", "PUT", "PATCH"].includes(method)) {
    bodyString = config.data ? JSON.stringify(config.data) : "";
    rawString = bodyString;
  } else {
    rawString = `${timestamp}:${nonce}`;
  }

  const signature = CryptoJS.HmacSHA256(rawString, secret).toString(
    CryptoJS.enc.Hex,
  );

  config.headers["X-Timestamp"] = timestamp;
  config.headers["X-Signature"] = signature;
  config.headers["X-Nonce"] = nonce;
  config.headers["x-timezone"] =
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  return config;
});

// ---------- RESPONSE INTERCEPTOR ----------
axiosApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Redirect to logout if unauthorised
    if (error?.response?.status === 401) {
      window.location.href = "/logout";
    }
    if (error?.response?.data) {
      return Promise.reject(error.response.data);
    }
    // Always return error.response so caller can handle
    return Promise.reject(error.response || error);
  },
);

// ---------- WRAPPER FUNCTIONS ----------
export const api = {
  get: (url, params = {}, config = {}) =>
    axiosApi.get(url, { params, ...config }).then((res) => res.data),

  post: (url, data = {}, config = {}) =>
    axiosApi.post(url, data, config).then((res) => res.data),

  put: (url, data = {}, config = {}) =>
    axiosApi.put(url, data, config).then((res) => res.data),

  patch: (url, data = {}, config = {}) =>
    axiosApi.patch(url, data, config).then((res) => res.data),

  delete: (url, data = {}, config = {}) =>
    axiosApi.delete(url, { data, ...config }).then((res) => res.data),

  formDataPost: (url, data, config = {}) =>
    axiosApi.post(url, data, config).then((res) => res.data),

  formDataPut: (url, data, config = {}) =>
    axiosApi.put(url, data, config).then((res) => res.data),

  formDataPatch: (url, data, config = {}) =>
    axiosApi.patch(url, data, config).then((res) => res.data),
  downloadExcel: (url, data = {}, config = {}) =>
    axiosApi
      .post(url, data, { ...config, responseType: "arraybuffer" })
      .then((res) => res.data),
  downloadCSV: (url, data = {}, config = {}) =>
    axiosApi
      .post(url, data, { ...config, responseType: "text" })
      .then((res) => res.data),
};

export default axiosApi;
